import { PayloadAction } from '@reduxjs/toolkit';
import { call, put, takeLatest } from 'redux-saga/effects';
import { toast } from 'react-toastify';
import { t } from 'i18next';
import { Response } from 'setup/axios/HttpResponse';
import { HTTP_RESPONSE_STATUS } from 'setup/constants/Http';
import { ToastDefaultConfig } from 'setup/toast/ToastConfig';
import {
  MerchantOnboardingPrefill,
  MerchantOnboardingResult,
  MerchantOnboardingStatusResult,
  RegenerateMerchantOnboardingSessionRequest,
  StartMerchantOnboardingRequest,
} from '../models';
import { merchantOnboardingApi } from './MerchantOnboardingApi';
import { merchantOnboardingActions } from './MerchantOnboardingSlice';

// ErrorsList.cs 1200-1208. These two mean the registration moved on without this tab
// noticing, so the screen re-reads status instead of showing an error.
const ER_MERCHANT_ONBOARDING_ALREADY_APPROVED = 1201;
const ER_MERCHANT_ONBOARDING_SESSION_UNAVAILABLE = 1204;

const RESOLVE_BY_REFRESH = [
  ER_MERCHANT_ONBOARDING_ALREADY_APPROVED,
  ER_MERCHANT_ONBOARDING_SESSION_UNAVAILABLE,
];

// The API returns 400 with the envelope in the body, so the code is on the rejected error.
const errorCodeOf = (error: any): number | undefined => error?.response?.data?.errorCode;

const messageOf = (error: any, fallbackKey: string): string =>
  error?.response?.data?.message || error?.message || t(fallbackKey);

function* fetchPrefill(): Generator<any, void, any> {
  try {
    const response: Response<MerchantOnboardingPrefill> = yield call(merchantOnboardingApi.getPrefill);
    if (response.status === HTTP_RESPONSE_STATUS.SUCCESS) {
      yield put(merchantOnboardingActions.fetchPrefillSuccess(response.data));
    } else {
      yield put(
        merchantOnboardingActions.fetchPrefillFailed(
          response.message || t('PAYMENT_GATEWAY_PAGE.ERRORS.FAILED_TO_LOAD_FORM')
        )
      );
    }
  } catch (error: any) {
    yield put(
      merchantOnboardingActions.fetchPrefillFailed(
        messageOf(error, 'PAYMENT_GATEWAY_PAGE.ERRORS.FAILED_TO_LOAD_FORM')
      )
    );
  }
}

function* fetchStatus(): Generator<any, void, any> {
  try {
    const response: Response<MerchantOnboardingStatusResult> = yield call(
      merchantOnboardingApi.getStatus
    );
    if (response.status === HTTP_RESPONSE_STATUS.SUCCESS) {
      yield put(merchantOnboardingActions.fetchStatusSuccess(response.data));
    } else {
      yield put(
        merchantOnboardingActions.fetchStatusFailed(
          response.message || t('PAYMENT_GATEWAY_PAGE.ERRORS.FAILED_TO_LOAD_STATUS')
        )
      );
    }
  } catch (error: any) {
    yield put(
      merchantOnboardingActions.fetchStatusFailed(
        messageOf(error, 'PAYMENT_GATEWAY_PAGE.ERRORS.FAILED_TO_LOAD_STATUS')
      )
    );
  }
}

function* startOnboarding(
  action: PayloadAction<StartMerchantOnboardingRequest>
): Generator<any, void, any> {
  try {
    const response: Response<MerchantOnboardingResult> = yield call(
      merchantOnboardingApi.startOnboarding,
      action.payload
    );
    if (response.status === HTTP_RESPONSE_STATUS.SUCCESS) {
      yield put(merchantOnboardingActions.startOnboardingSuccess(response.data));
      yield put(merchantOnboardingActions.fetchStatusStart({ silent: true }));
    } else {
      const message =
        response.message || t('PAYMENT_GATEWAY_PAGE.ERRORS.FAILED_TO_START_REGISTRATION');
      toast.error(message, ToastDefaultConfig());
      yield put(merchantOnboardingActions.startOnboardingFailed(message));
    }
  } catch (error: any) {
    const { message, resolveByRefresh } = classifyActionError(
      error,
      'PAYMENT_GATEWAY_PAGE.ERRORS.FAILED_TO_START_REGISTRATION'
    );
    yield put(merchantOnboardingActions.startOnboardingFailed(message));
    if (resolveByRefresh) {
      yield put(merchantOnboardingActions.fetchStatusStart({ silent: true }));
    }
  }
}

function* regenerateSession(
  action: PayloadAction<RegenerateMerchantOnboardingSessionRequest>
): Generator<any, void, any> {
  try {
    const response: Response<MerchantOnboardingResult> = yield call(
      merchantOnboardingApi.regenerateSession,
      action.payload
    );
    if (response.status === HTTP_RESPONSE_STATUS.SUCCESS) {
      // Same shape and consequence as a start: the page opens the returned URL.
      yield put(merchantOnboardingActions.startOnboardingSuccess(response.data));
      yield put(merchantOnboardingActions.fetchStatusStart({ silent: true }));
    } else {
      const message = response.message || t('PAYMENT_GATEWAY_PAGE.ERRORS.FAILED_TO_OPEN_LINK');
      toast.error(message, ToastDefaultConfig());
      yield put(merchantOnboardingActions.startOnboardingFailed(message));
    }
  } catch (error: any) {
    const { message, resolveByRefresh } = classifyActionError(
      error,
      'PAYMENT_GATEWAY_PAGE.ERRORS.FAILED_TO_OPEN_LINK'
    );
    yield put(merchantOnboardingActions.startOnboardingFailed(message));

    // The lead outlived the provider's 30 days, so no session can be minted against it.
    if (errorCodeOf(error) === ER_MERCHANT_ONBOARDING_SESSION_UNAVAILABLE) {
      yield put(merchantOnboardingActions.requireNewRegistration());
      yield put(merchantOnboardingActions.fetchPrefillStart());
      return;
    }

    if (resolveByRefresh) {
      yield put(merchantOnboardingActions.fetchStatusStart({ silent: true }));
    }
  }
}

// Decides whether a failed start/regenerate is something the operator needs to see.
function classifyActionError(error: any, fallbackKey: string) {
  const code = errorCodeOf(error);
  const message = messageOf(error, fallbackKey);
  const resolveByRefresh = code !== undefined && RESOLVE_BY_REFRESH.includes(code);

  if (!resolveByRefresh) {
    toast.error(message, ToastDefaultConfig());
  }

  return { message, resolveByRefresh };
}

export function* merchantOnboardingSaga() {
  yield takeLatest(merchantOnboardingActions.fetchPrefillStart.type, fetchPrefill);
  yield takeLatest(merchantOnboardingActions.fetchStatusStart.type, fetchStatus);
  yield takeLatest(merchantOnboardingActions.startOnboardingStart.type, startOnboarding);
  yield takeLatest(merchantOnboardingActions.regenerateSessionStart.type, regenerateSession);
}

export default merchantOnboardingSaga;
